import { InputField } from '@components/form/input-field/InputField';
import { TextAreaField } from '@components/form/text-area/TextAreaField';
import { InputCharsCount } from '@components/input-chars-counter/InputCharsCount';
import { TooltipInfo } from '@components/tooltip-info/TooltipInfo';
import { ThoughtLeadershipExpertName } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/expert-name-input/ThoughtLeadershipExpertName';
import { ThoughtLeadershipFormType } from '@mailings/components/creators/personalized-mailing-creator-wizard/types/mailingObjectivesForm.types';
import { FIELD_CONFIGS } from '@mailings/components/creators/personalized-mailing-creator-wizard/validations/getBaseObjectivesFormValidationSchema.validations';
import { Text } from '@semcore/ui/typography';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const ThoughtLeadershipFormFields = () => {
  const { t } = useTranslation(['mailings']);

  const { register, control } = useFormContext<ThoughtLeadershipFormType>();

  const expertIntroduction = useWatch({ control, name: 'expertIntroduction' }) || '';

  const expertReasoning = useWatch({ control, name: 'expertReasoning' }) || '';

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="expert-name-input"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.tooltip',
            )}
          </TooltipInfo>
        </div>
        <ThoughtLeadershipExpertName />
      </div>

      <div className="flex flex-col gap-2">
        <Text
          htmlFor="expert-role-input"
          tag="label">
          {t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertRole.label',
          )}
        </Text>
        <InputField
          autoComplete="off"
          data-test="expert-role-input"
          id="expert-role-input"
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertRole.placeholder',
          )}
          size="l"
          {...register('expertRole')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="expert-introduction-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertIntroduction.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertIntroduction.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertIntroduction.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={expertIntroduction.length}
            maxCount={FIELD_CONFIGS.EXPERT_COMMENT.INTRODUCTION}
            size="l"
          />
        </div>
        <div>
          <TextAreaField
            data-test="expert-introduction-textarea"
            id="expert-introduction-textarea"
            maxRows={6}
            minRows={4}
            placeholder={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertIntroduction.placeholder',
            )}
            size="l"
            {...register('expertIntroduction')}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="expert-reasoning-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertReasoning.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertReasoning.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertReasoning.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={expertReasoning.length}
            maxCount={FIELD_CONFIGS.EXPERT_COMMENT.REASONING}
            size="l"
          />
        </div>
        <TextAreaField
          data-test="expert-reasoning-textarea"
          id="expert-reasoning-textarea"
          maxRows={6}
          minRows={4}
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertReasoning.placeholder',
          )}
          size="l"
          {...register('expertReasoning')}
        />
      </div>
    </div>
  );
};
